<script lang="ts">
	//# SVELTE
	import { onMount } from 'svelte';
	import { format } from 'svelte-i18n';
	import { confirmPasswordReset } from '../services/http';
	//Design system components
	import { Button, Text, Alert } from '@Shelly/shelly-design-system';
	import type { InputPropsTypes } from '@Shelly/shelly-design-system';
	import PasswordUtility from 'src/components/PasswordUtility.svelte';
	import { push, querystring } from 'svelte-spa-router';
	import { validateAllowedSymbols } from 'src/helpers/validators';
	import { passwordHasAllMandatorySymbols, capFirstChar } from 'src/helpers/utilities';
	import { MIN_PASSWORD_LENGTH } from 'src/constants';
	import { user_data } from 'src/stores/account/user';

	const searchParams = new URLSearchParams($querystring);

	let pass_error,
		token = searchParams.get('token') || '';

	let loading = false;
	let alertError = '';
	let actionError = '';
	let isPasswordButtonDisabled: boolean = false;
	$: isPasswordButtonDisabled = alertError.length > 0 ? true : false;

	let inputProps: {
		size: InputPropsTypes['size'];
		icon: InputPropsTypes['icon'];
		value: InputPropsTypes['value'];
		label: InputPropsTypes['label'];
		type: InputPropsTypes['type'];
		disabled: InputPropsTypes['disabled'];
		class: string;
		autocomplete: string;
	} = {
		size: 'l',
		class: 'mb-m',
		disabled: loading || isPasswordButtonDisabled,
		autocomplete: 'new-password',
		value: '',
		label: 'new_password',
		icon: 'fas fa-lock-keyhole',
		type: 'password',
	};

	$: error = pass_error;

	$: alertErrorMessage = alertError
		? $format(`alert_error_${alertError}`, {
				values: { minPassLength: MIN_PASSWORD_LENGTH },
			})
		: isPasswordButtonDisabled
			? $format('alert_error_missing_token_data', {
					default: 'Missing token data!',
				})
			: '';

	onMount(() => {
		if (!$querystring.includes('token=')) {
			isPasswordButtonDisabled = true;
		}
	});

	const clear_errors = () => {
		if (pass_error) {
			pass_error = undefined;
		}
	};

	const submit_req = async () => {
		await confirmPasswordReset(token, inputProps.value).then((res) => {
			if (res && res.error) {
				alertError = Object.keys(res?.error?.response?.data?.errors)[0];
				actionError = alertError === 'spwd' || $user_data?.email ? 'login' : 'forgot';
			}
		});
	};

	const check_password = async () => {
		if (inputProps.value.length < MIN_PASSWORD_LENGTH) {
			pass_error = $format('short_password', {
				values: { minPassLength: MIN_PASSWORD_LENGTH },
				default: `Your password should be at least ${MIN_PASSWORD_LENGTH} symbols`,
			});
		} else if (validateAllowedSymbols(inputProps.value)) {
			pass_error = $format('password_validate_allowed_symbols', {
				default:
					'Please use only letters (a – z and A – Z), numbers (0 – 9) and uppercase characters such as !@#$%^&*.()',
			});
		} else if (!passwordHasAllMandatorySymbols(inputProps.value)) {
			pass_error = $format('password_error_requirements', {
				default: "The password doesn't meet the requirements!",
			});
		} else {
			clear_errors();
			await submit_req();
		}
	};

	$: {
		if (inputProps.value) {
			alertError = '';
		}
	}
</script>

<Text element="h1" class="text-capitalize  mb-m">
	{$format('text_title_reset_new_password', { default: 'New password' })}
</Text>

<Text class="text-capitalize mb-xl ">
	{$format('text_description_reset_new_password', {
		default: 'You have requested to reset your password',
	})}
</Text>

{#if isPasswordButtonDisabled || alertError !== ''}
	<div class="mb-m">
		<Alert
			type="danger"
			heading={$format('alert_error_on_resetting_password', {
				default: 'Error on resetting password!',
			})}
			isHeadingBold
			message={alertErrorMessage}
			cta={{
				title:
					actionError === 'login'
						? $format('alert_cta_continue_to_log_in', {
								default: 'Continue to log in',
							})
						: $format('go_to_forgotten_password', {
								default: 'Go to forgotten password',
							}),
			}}
			on:cta_click={() => {
				if (actionError === 'login') {
					push('/login');
				} else {
					push('/login?forgotten-password');
				}
			}}
		/>
	</div>
{/if}

<PasswordUtility bind:inputProps bind:error on:focus={() => clear_errors()} on:enter={check_password} />

<Button
	{loading}
	disabled={loading || isPasswordButtonDisabled}
	class="mb-m mt-m"
	variant="primary"
	width="full"
	on:click={check_password}
>
	{capFirstChar($format('button_reset', { default: 'reset' }))}
</Button>

<Button
	variant="secondary"
	width="full"
	on:click={() => {
		push('/login');
		alertError = '';
	}}>{capFirstChar($format('button_cancel', { default: 'cancel' }))}</Button
>
